import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /teams-integration: Microsoft Teams automation with ClearMakes Inbox. Rewritten 2026-09-29
// for today's product (SEO run: gtm/products/clearmakes-inbox/seo/). Microsoft facts opened 2026-09-29.

const PATH = "/teams-integration";

const faqs = [
  {
    q: "Can a Teams message start a workflow?",
    a: "Yes. A workflow can start when a message arrives in Teams, then email someone, create a task, or update HubSpot or Notion.",
  },
  {
    q: "Does my IT admin need to approve it?",
    a: "Sometimes. Many Microsoft 365 organisations require an admin to approve new apps. If Microsoft asks for approval when you connect Teams, send the request to your IT admin.",
  },
  {
    q: "Isn't this what Power Automate does?",
    a: "Power Automate can post to Teams too, and its basic licence is included for work or school accounts. Inbox is for people who would rather describe the job in a sentence, or who mix Microsoft apps with Gmail, Slack or HubSpot.",
  },
  {
    q: "What does it cost?",
    a: "Free: 3 apps, 2 new workflows a month and 100 runs a month. Starter is $29 a month for 5,000 runs. Enterprise is $39 a seat a month with company sign-in (SSO).",
  },
];

const TeamsIntegration = () => (
  <>
    <PageMeta
      title="Microsoft Teams Automation for Email | ClearMakes Inbox"
      description="Post important emails to Microsoft Teams channels with a summary, or start a workflow from a Teams message. Described in one sentence. Free to try."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Microsoft Teams", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Microsoft Teams"
        title="Microsoft Teams automation for the emails that matter"
        lead="Post important emails to the right Teams channel with a short summary, or let a Teams message start a workflow in your other apps."
        secondary={{ label: "See every integration", href: "/integrations" }}
      />

      <WhatIs title="What does ClearMakes Inbox do with Microsoft Teams?">
        ClearMakes Inbox connects to Microsoft Teams so your workflows can post to channels, and so a new Teams message can start a workflow. The
        most common use is posting important Outlook or Gmail emails to a channel with a summary. You describe each workflow in one sentence and
        check the steps. There is a free plan.
      </WhatIs>

      <Band tone="pale" title="What a Teams workflow can do" answer="It posts to channels when something arrives by email, and it can start from a Teams message. Other apps can join the same run.">
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When the sales@ mailbox gets a new enquiry, post the company and what they want to the Sales channel."
            steps={["When an email arrives in Outlook", "Pull out the details: company, request", "Post to the Sales channel in Teams"]}
          />
          <WorkflowCard
            sentence="When someone in the Support channel writes “urgent”, email the on-call person."
            steps={["When a message arrives in Teams", "Check it says urgent", "Send an email with Outlook"]}
          />
        </div>
      </Band>

      <Band tone="white" title="How to connect Microsoft Teams" answer="Sign in with Microsoft from the Apps page. Your admin may need to approve the app the first time.">
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start from <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Open Apps and connect Microsoft Teams", body: "Microsoft shows the permissions before you agree." },
            { title: "Describe a workflow and check the steps", body: "Name the team and channel in the sentence, then switch it on." },
          ]}
        />
      </Band>

      <Band tone="pale" title="Other ways to automate Teams" answer="Teams channels can take email at their own address, and Power Automate can post to them. Both come with many Microsoft 365 subscriptions.">
        <Table
          caption="Ways to automate Microsoft Teams"
          head={["Way", "What it costs", "Good at", "Watch out for"]}
          rows={[
            ["Channel email address", "Free with Teams", "Copying emails into a channel", "Admin must switch it on; 20 attachments of up to 10 MB; not on Government plans"],
            ["Power Automate", "Free licence for work or school accounts (standard connectors); Premium $15 a user a month, yearly", "Teams action “Post message in a chat or channel”", "You build each flow; extra steps to filter or summarise"],
            ["ClearMakes Inbox", "Free: 3 apps, 100 runs a month. Starter: $29 a month for 5,000 runs", "One sentence; choosing, summarising and other apps in one run", "Only starts from messages; newer company"],
          ]}
          note={
            <>
              Checked on 29 September 2026:{" "}
              <Src href="https://support.microsoft.com/en-us/office/send-an-email-to-a-channel-in-microsoft-teams-d91db004-d9d7-4a47-82e6-fb1b16dfd51e">Microsoft Support, email to a channel</Src>,{" "}
              <Src href="https://learn.microsoft.com/en-us/connectors/teams/">Microsoft Teams connector</Src>,{" "}
              <Src href="https://learn.microsoft.com/en-us/power-platform/admin/power-automate-licensing/types">Power Automate licence types</Src>.
            </>
          }
        />
      </Band>

      <Band tone="white" title="Limits, and who this is not for" answer="If you only need copies of emails in a channel and you're on Microsoft 365, use the channel address or Power Automate." narrow>
        <List
          items={[
            "Not a Teams bot that chats with your team; for an assistant that answers questions, look at ClearMakes Agents.",
            "AI summaries can be wrong now and then; check the first posts.",
            "Government (GCC) tenants: not tested.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/outlook-to-teams", label: "Post Outlook emails to Teams", desc: "Three ways, free ones first." },
          { href: "/outlook-integration", label: "Outlook email automation", desc: "What a workflow can do with new Outlook email." },
          { href: "/slack-integration", label: "Slack automation", desc: "If your team is on Slack." },
          { href: "/for-remote-teams", label: "For remote teams", desc: "The right email in the right channel." },
          { href: "/pricing", label: "Pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Put the emails that matter in the right Teams channel." line="Connect Teams and describe your first workflow in one sentence." />
    </main>
    <Footer />
  </>
);

export default TeamsIntegration;
